/**
 * ProductCardSample — live WYSIWYG sample tile used inside the Visual
 * Builder's Product Card editor.
 *
 * This component MIRRORS the runtime consumer at
 * /app/frontend/src/components/customer/ProductCard.jsx so what the
 * super-admin sees in the builder matches what the customer will see on
 * the live site — every field on the `card` blob is respected here.
 *
 * IMPORTANT: This is a preview tile — it does NOT trigger cart / auth /
 * network calls. All product data is hard-coded sample data.
 */
import { Bell, Package, Plus, Star, ShoppingCart } from "lucide-react";
import { PRODUCT_CARD_DEFAULTS } from "@/lib/visualBuilder/productCardSchema";

const SAMPLE = {
  name: "Amul Taaza Toned Milk",
  weight: 500,
  weight_unit: "ml",
  mrp: 32,
  selling_price: 26,
  rating: 4.5,
  review_count: 1240,
  sold_count: 12000,
  thumbnail_url: "https://cdn.dummyjson.com/product-images/kitchen-accessories/water-purifier/1.webp",
};

export default function ProductCardSample({ card = {}, testId }) {
  const c = { ...PRODUCT_CARD_DEFAULTS, ...(card || {}) };

  const discount = c.show_mrp && SAMPLE.mrp > SAMPLE.selling_price
    ? Math.round(((SAMPLE.mrp - SAMPLE.selling_price) / SAMPLE.mrp) * 100)
    : 0;

  const money = (v) => (c.currency_position === "after"
    ? `${Math.round(v)}${c.currency_symbol}` : `${c.currency_symbol}${Math.round(v)}`);

  const shadow = { none: "none", soft: "0 1px 2px rgba(0,0,0,0.04)", strong: "0 4px 14px rgba(0,0,0,0.08)" }[c.shadow] || "none";

  const badgePosCls = {
    "top-left": "top-2 left-2", "top-right": "top-2 right-2",
    "bottom-left": "bottom-2 left-2", "bottom-right": "bottom-2 right-2",
  }[c.badge_position || "top-left"];

  const deliveryPosCls = {
    "top-left":  "top-2 left-2",  "top-right": "top-2 right-2",
    "bottom-left": "bottom-2 left-2", "bottom-right": "bottom-2 right-2",
    "inside-image": "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
    "outside-image": "-bottom-2 left-1/2 -translate-x-1/2",
  }[c.delivery_badge_position || "top-right"];

  const badgeStyleCls = {
    gradient: "bg-gradient-to-b from-[#E32636] to-[#c11e2b] text-white",
    solid:    "bg-[#E32636] text-white",
    outline:  "bg-white text-[#E32636] border border-[#E32636]",
  }[c.badge_style || "gradient"];

  const discShapeRadius = ({
    rounded: c.discount_radius ?? 8, pill: 999, rectangle: 0, circle: 999, tag: c.discount_radius ?? 6,
  })[c.discount_shape || "rounded"];

  const discountLabelBig = c.discount_label === "OFF" ? "OFF" :
    (c.discount_label || "% OFF").replace(/OFF/i, "").trim() || "%";
  const discountLabelSub = c.discount_label === "%" ? "" : "OFF";

  const addShadowStyle = ({
    none: "none",
    soft: "0 1px 3px rgba(0,0,0,0.08)",
    strong: "0 6px 16px rgba(0,0,0,0.15)",
    glow: `0 6px 22px -4px ${c.add_border_color || "rgba(227,38,54,0.55)"}`,
  })[c.add_shadow] || "none";

  const addNode =
    c.add_button_variant === "plus"      ? <Plus className="w-4 h-4" /> :
    c.add_button_variant === "cart"      ? <ShoppingCart className="w-4 h-4" /> :
    c.add_button_variant === "text_plus" ? (
      <span className="inline-flex items-center gap-0.5">{c.add_button_text || "ADD"} <Plus className="w-3 h-3" /></span>
    ) : (c.add_button_text || "ADD");

  const imgObjectPos =
    c.image_position === "top"    ? "center top" :
    c.image_position === "bottom" ? "center bottom" :
    c.image_position === "custom" ? `center ${c.image_offset_y ?? 50}%` :
                                    "center center";

  return (
    <div
      className="relative bg-white border border-gray-200 overflow-hidden flex flex-col"
      style={{ borderRadius: c.radius, boxShadow: shadow, width: 200 }}
      data-testid={testId || "vb-product-sample"}
    >
      {/* Image */}
      <div className="relative bg-white" style={{ aspectRatio: c.image_aspect, padding: c.image_padding }}>
        <div className="w-full h-full rounded-xl overflow-hidden bg-[hsl(30_25%_98%)] grid place-items-center">
          {SAMPLE.thumbnail_url ? (
            <img
              src={SAMPLE.thumbnail_url} alt={SAMPLE.name}
              className="w-full h-full object-contain"
              style={{ objectPosition: imgObjectPos }}
            />
          ) : <Package className="w-8 h-8 text-primary/30" />}
        </div>

        {c.show_discount_badge && discount > 0 && (
          <div
            className={`absolute ${badgePosCls} overflow-hidden ${c.discount_shape === "circle" ? "grid place-items-center" : ""}`}
            style={{
              background: c.discount_bg_color || undefined,
              color: c.discount_text_color || undefined,
              borderRadius: discShapeRadius,
              width: c.discount_width || undefined,
              height: c.discount_height || undefined,
              boxShadow: { none: "none", soft: "0 2px 6px rgba(0,0,0,0.12)", strong: "0 6px 14px rgba(0,0,0,0.18)" }[c.discount_shadow || "soft"],
            }}
          >
            <div
              className={`${c.discount_bg_color ? "" : badgeStyleCls} px-2 py-0.5 text-center leading-tight ${c.discount_shape === "circle" ? "rounded-full aspect-square grid place-items-center" : ""}`}
              style={{
                fontSize: `${c.discount_font_size}px`,
                fontWeight: c.discount_font_weight,
                color: c.discount_text_color || undefined,
                background: c.discount_bg_color || undefined,
              }}
            >
              <div className="tracking-tight" style={{ fontWeight: c.discount_font_weight }}>{discount}{discountLabelBig}</div>
              {discountLabelSub && c.discount_shape !== "circle" && (
                <div className="text-[8px] font-bold uppercase tracking-wider opacity-95 -mt-0.5">{discountLabelSub}</div>
              )}
            </div>
          </div>
        )}

        {c.show_delivery_badge && (
          <div
            className={`absolute ${deliveryPosCls} inline-flex items-center gap-1 bg-white/95 backdrop-blur font-semibold text-slate-900 px-1.5 py-0.5 shadow-sm`}
            style={{
              background: c.delivery_bg_color || undefined,
              color: c.delivery_text_color || undefined,
              borderRadius: c.delivery_radius != null ? c.delivery_radius : undefined,
              fontSize: `${c.delivery_font_size}px`,
            }}
          >
            {c.delivery_show_icon && <Bell className="w-2.5 h-2.5" style={{ color: c.delivery_text_color || undefined }} />}
            {c.delivery_badge_text}
          </div>
        )}
      </div>

      {/* Body */}
      <div
        className="flex flex-col flex-1 gap-0.5"
        style={{
          paddingLeft: c.padding_x, paddingRight: c.padding_x,
          paddingTop: c.padding_y, paddingBottom: c.padding_y,
        }}
      >
        <h3
          className="font-semibold text-slate-800"
          style={{
            fontSize: `${c.name_size}px`, fontWeight: c.name_weight, lineHeight: 1.2,
            display: "-webkit-box", WebkitLineClamp: c.name_lines, WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >{SAMPLE.name}</h3>

        <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
          {c.show_weight && <span>{SAMPLE.weight} {SAMPLE.weight_unit}</span>}
        </div>

        {(c.show_rating || c.show_sold_count) && (
          <div className="flex items-center gap-2 mt-0.5 text-[10px]">
            {c.show_rating && (
              <span className="inline-flex items-center gap-0.5 rounded bg-emerald-600 text-white px-1 py-[1px] font-bold tabular-nums leading-none">
                <Star className="w-2.5 h-2.5 fill-white" />
                {SAMPLE.rating.toFixed(1)}
                <span className="opacity-90 font-medium ml-0.5">(1.2k)</span>
              </span>
            )}
            {c.show_sold_count && <span className="text-slate-500 truncate">12k sold</span>}
          </div>
        )}

        <div className="flex items-end justify-between gap-1 mt-auto pt-1">
          <div className="min-w-0 flex-1 flex flex-col leading-[1.15]">
            <span
              className="tabular-nums truncate"
              style={{ fontSize: `${c.price_size}px`, fontWeight: c.price_weight, color: c.price_color || undefined }}
            >{money(SAMPLE.selling_price)}</span>
            {c.show_mrp && c.show_discount_pct && discount > 0 && (
              <span
                className="tabular-nums truncate"
                style={{
                  fontSize: `${c.mrp_size}px`, fontWeight: c.mrp_weight,
                  color: c.mrp_color || "rgba(100,116,139,0.8)",
                  textDecoration: "line-through", textDecorationThickness: `${c.mrp_strike_width}px`,
                }}
              >{money(SAMPLE.mrp)}</span>
            )}
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center border font-bold tracking-wide"
            style={{
              width: c.button_width, height: c.button_height, borderRadius: c.button_radius,
              background: c.add_bg_color || "#fff5f5",
              borderColor: c.add_border_color || "#E32636",
              color: c.add_text_color || "#E32636",
              boxShadow: addShadowStyle,
              fontSize: `${c.add_font_size}px`, fontWeight: c.add_font_weight,
            }}
          >{addNode}</button>
        </div>
      </div>
    </div>
  );
}
